import type { LineCitation } from '../../services/ai/citations/citationTypes'

export type LineTextSegment = { kind: 'text'; text: string }
export type LineCiteSegment = { kind: 'cite'; citationId: string; index: number }
export type LineSegment = LineTextSegment | LineCiteSegment

const CITE_TOKEN = /\[\[cite:(\d+)\]\]/g

/**
 * Parses the model-produced message text into an array of text and citation
 * segments. Handles:
 *  - Adjacent tokens ("...deadline[[cite:1]][[cite:2]]") → two chip segments.
 *  - Unresolved tokens → dropped silently.
 *  - Duplicate references to the same citation → same 1-based user-facing index.
 *  - Empty messages / missing citations arrays.
 *  - Unicode / emoji before or after tokens → preserved verbatim in text segments.
 */
export const parseCitationSegments = (
  text: string,
  citations: readonly LineCitation[] | undefined | null,
): LineSegment[] => {
  if (!text) return []
  const cites = citations ?? []
  if (!cites.length || !text.includes('[[cite:')) {
    return [{ kind: 'text', text }]
  }

  const segments: LineSegment[] = []
  let cursor = 0
  CITE_TOKEN.lastIndex = 0
  for (let match = CITE_TOKEN.exec(text); match !== null; match = CITE_TOKEN.exec(text)) {
    const [full, indexStr] = match
    const start = match.index
    const oneBasedIndex = Number.parseInt(indexStr ?? '0', 10)
    const citation = oneBasedIndex >= 1 ? cites[oneBasedIndex - 1] : undefined
    if (start > cursor) {
      segments.push({ kind: 'text', text: text.slice(cursor, start) })
    }
    if (citation) {
      segments.push({ kind: 'cite', citationId: citation.id, index: oneBasedIndex })
    }
    cursor = start + full.length
  }
  if (cursor < text.length) {
    segments.push({ kind: 'text', text: text.slice(cursor) })
  }
  // Merge adjacent text segments produced by dropped citations.
  const merged: LineSegment[] = []
  segments.forEach((segment) => {
    const last = merged[merged.length - 1]
    if (segment.kind === 'text' && last && last.kind === 'text') {
      last.text = `${last.text}${segment.text}`
    } else {
      merged.push({ ...segment })
    }
  })
  return merged
}

/**
 * Total character count when reading the segment array as raw text — cite
 * segments count as their marker length (2 characters) so the typewriter
 * cursor advances predictably. Chips reveal atomically when the cursor
 * enters their segment.
 */
export const segmentTotalLength = (segments: readonly LineSegment[]): number =>
  segments.reduce((acc, segment) => acc + (segment.kind === 'text' ? segment.text.length : 2), 0)

/**
 * Given a segment array and a numeric "revealed character count" cursor,
 * produce the visible slice for each segment. Text segments are truncated
 * to the revealed prefix; cite segments are either fully visible or hidden.
 */
export interface RevealedSegment {
  segment: LineSegment
  visibleText: string
  isRevealed: boolean
}

export const revealSegments = (
  segments: readonly LineSegment[],
  revealedCount: number,
): RevealedSegment[] => {
  const output: RevealedSegment[] = []
  let consumed = 0
  segments.forEach((segment) => {
    if (segment.kind === 'text') {
      const remaining = Math.max(0, revealedCount - consumed)
      const visible = segment.text.slice(0, Math.min(remaining, segment.text.length))
      output.push({ segment, visibleText: visible, isRevealed: visible.length > 0 })
      consumed += segment.text.length
    } else {
      const isRevealed = consumed < revealedCount
      output.push({ segment, visibleText: '', isRevealed })
      consumed += 2
    }
  })
  return output
}
